// src/components/OpportuniteFoodinnovModal.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import "../styles/OpportunitesModal.css";

export interface FirestoreOpportunity {
  id?: string;
  companyId: string;
  contact: string;
  type: string;
  amount: number;
  date: string;
  createdAt?: string;
  qte?: number;
  pu?: number;
  remise?: number;
  ttc?: number;
  description?: string;
  status?: string;
  isContratCadre?: boolean;
  tva?: number;
}

interface OtherContact {
  civilite?: string;
  nom: string;
  prenom?: string;
  tel?: string;
  email?: string;
}

interface CompanyRecord {
  principalGender?: string;
  principalContact: string;
  principalFirstName?: string;
  otherContacts?: OtherContact[];
  opportunities?: FirestoreOpportunity[];
  [key: string]: any;
}

interface CompaniesData {
  [companyName: string]: CompanyRecord;
}

interface OpportuniteFoodinnovModalProps {
  show: boolean;
  handleClose: () => void;
  onSave: (opp: FirestoreOpportunity) => void;
  onUpdate: (oldOpp: FirestoreOpportunity, newOpp: FirestoreOpportunity) => void;
  editingOpportunity: FirestoreOpportunity | null;
  opportunityTypes?: string[]; // Types dynamiques passés depuis la page parente
  preselectedCompanyId?: string | null; // Société pré-sélectionnée depuis la navigation
}



const OpportuniteFoodinnovModal: React.FC<OpportuniteFoodinnovModalProps> = ({
  show,
  handleClose,
  onSave,
  onUpdate,
  editingOpportunity,
  opportunityTypes = [],
  preselectedCompanyId
}) => {
  const [companiesData, setCompaniesData] = useState<CompaniesData>({});
  const [companyId, setCompanyId] = useState("");
  const [contact, setContact] = useState("");
  const [type, setType] = useState("");
  const [qte, setQte] = useState<string>("1");
  const [pu, setPu] = useState<number>(0);
  const [remise, setRemise] = useState<string>("0");
  const [date, setDate] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [status, setStatus] = useState<string>("En Cours");
  const [manualAmount, setManualAmount] = useState<boolean>(false);
  const [amountInput, setAmountInput] = useState<string>("");
  const [isContratCadre, setIsContratCadre] = useState<boolean>(false);
  const [tva, setTva] = useState<number>(20);
  const [tvaIsCustom, setTvaIsCustom] = useState(false);

  const computedAmount = Number(qte.replace(',', '.')) * pu * (1 - (Number(remise.replace(',', '.')) || 0) / 100);
  const amount = manualAmount ? Number(amountInput.replace(',', '.')) : computedAmount;
  const ttc = amount * (1 + tva / 100);

  useEffect(() => {
    if (!show) return;
    const ref = doc(db, "prospects", "foodinnovCompanies");
    const unsub = onSnapshot(ref, (snap) => {
      setCompaniesData(snap.exists() ? (snap.data() as CompaniesData) : {});
    });
    return () => unsub();
  }, [show]);

  useEffect(() => {
    if (editingOpportunity) {
      setCompanyId(editingOpportunity.companyId);
      setContact(editingOpportunity.contact);
      setType(editingOpportunity.type);
      setQte(editingOpportunity.qte?.toString() || "1");
      setPu(editingOpportunity.pu || 0);
      setRemise(editingOpportunity.remise?.toString() || "0");
      setDate(editingOpportunity.date);
      setDescription(editingOpportunity.description || "");
      setStatus(editingOpportunity.status || "En Cours");
      setManualAmount(false);
      setAmountInput(editingOpportunity.amount?.toString() || "");
      setIsContratCadre(editingOpportunity.isContratCadre || false);
      const tvaValue = editingOpportunity.tva !== undefined ? editingOpportunity.tva : 20;
      setTva(tvaValue);
      setTvaIsCustom(tvaValue !== 20);
    } else {
      setCompanyId("");
      setContact("");
      setType("");
      setQte("1");
      setPu(0);
      setRemise("0");
      setDate("");
      setDescription("");
      setStatus("En Cours");
      setManualAmount(false);
      setAmountInput("");
      setIsContratCadre(false);
      setTva(20);
      setTvaIsCustom(false);
    }
  }, [editingOpportunity, show]);

  // Pré-sélectionner la société si elle est passée en prop
  useEffect(() => {
    if (show && preselectedCompanyId && !editingOpportunity && Object.keys(companiesData).length > 0) {
      // Chercher la société par id ou par name (car la clé peut être différente)
      const foundCompany = Object.keys(companiesData).find(c => 
        c === preselectedCompanyId || 
        c.replace(/[./]/g, "_") === preselectedCompanyId.replace(/[./]/g, "_")
      );
      
      if (foundCompany) {
        setCompanyId(foundCompany);
        // Sélectionner automatiquement le premier contact s'il existe
        const comp = companiesData[foundCompany];
        if (comp.principalContact) {
          const civ = comp.principalGender || "Mr";
          const full = `${civ} ${comp.principalFirstName || ""} ${comp.principalContact}`.trim();
          setContact(full);
        } else if (Array.isArray(comp.otherContacts) && comp.otherContacts.length > 0) {
          const c = comp.otherContacts[0];
          const civ = c.civilite || "Mr";
          const name = `${civ} ${c.prenom || ""} ${c.nom}`.trim();
          setContact(name);
        }
      }
    }
  }, [show, preselectedCompanyId, editingOpportunity, companiesData]);

  useEffect(() => {
    if (!manualAmount) {
      setAmountInput(computedAmount.toFixed(2));
    } else {
      const q = Number(qte.replace(',', '.'));
      if (q > 0 && pu > 0) {
        const newRemise = 100 * (1 - (Number(amountInput.replace(',', '.')) / (q * pu)));
        setRemise(Number.isFinite(newRemise) ? Math.max(0, Math.min(100, Number(newRemise.toFixed(2)))).toString() : "0");
      }
    }
    // eslint-disable-next-line
  }, [qte, pu, remise, manualAmount, amountInput]);

  const contactsList: string[] = (() => {
    const comp = companiesData[companyId];
    if (!comp) return [];
    const list: string[] = [];

    if (comp.principalContact) {
      const civ = comp.principalGender || "Mr";
      const full = `${civ} ${comp.principalFirstName || ""} ${comp.principalContact}`.trim();
      list.push(full);
    }

    if (Array.isArray(comp.otherContacts)) {
      comp.otherContacts.forEach((c) => {
        const civ = c.civilite || "Mr";
        const name = `${civ} ${c.prenom || ""} ${c.nom}`.trim();
        list.push(name);
      });
    }

    return list;
  })();

  const handleQteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
      setQte(value || '1');
      setManualAmount(false);
    }
  };

  const handlePuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPu(Number(e.target.value));
    setManualAmount(false);
  };

  const handleRemiseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
      setRemise(value);
      setManualAmount(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountInput(e.target.value);
    setManualAmount(true);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const opp: FirestoreOpportunity = {
      id: editingOpportunity?.id || `opp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      companyId,
      contact,
      type: type, // Toujours garder le type original, ne pas le remplacer par "Contrat Cadre"
      amount: isContratCadre ? 0 : Number(amountInput.replace(',', '.')),
      remise: Number(remise.replace(',', '.')),
      ttc: isContratCadre ? 0 : Number(ttc.toFixed(2)),
      qte: Number(qte.replace(',', '.')),
      pu,
      date,
      description,
      status,
      createdAt: editingOpportunity?.createdAt || new Date().toISOString(),
      isContratCadre,
      tva,
    };
    if (editingOpportunity) onUpdate(editingOpportunity, opp);
    else onSave(opp);
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{editingOpportunity ? "Modifier une demande" : "Ajouter une demande"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom de la société</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} required>
              <option value="" disabled>Choisir une société…</option>
              {Object.keys(companiesData)
                .sort((a, b) => a.localeCompare(b, 'fr'))
                .map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label>Contact</label>
            <select
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
              disabled={!companyId}
            >
              <option value="" disabled>{companyId ? "Choisir un contact…" : "Sélectionnez d'abord une société"}</option>
              {contactsList.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Type de demande :</label>
            <select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="" disabled>-- Sélectionnez un type --</option>
              {opportunityTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Description (facultative)</label>
            <textarea
              rows={2}
              placeholder="Détails ou précisions utiles sur cette opportunité"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label>Quantité :</label>
              <input 
                type="text" 
                value={qte} 
                onChange={handleQteChange} 
                placeholder="1"
                inputMode="decimal"
              />
            </div>
            <div className="form-group">
              <label>PU (HT) :</label>
              <input type="number" value={pu} onChange={handlePuChange} min={0} />
            </div>
            <div className="form-group">
              <label>Remise (%) :</label>
              <input type="text" value={remise} onChange={handleRemiseChange} inputMode="decimal" />
            </div>
          </div>

          <div className="form-row-3">
            <div className="form-group"></div>
            <div className="form-group">
              <label>Montant (HT) :</label>
              <input type="text" value={amountInput} onChange={handleAmountChange} />
            </div>
            <div className="form-group">
              <p style={{ marginTop: "0.5rem", fontWeight: "bold" }}>
                Montant (TTC) : <span style={{ color: "green" }}>{ttc.toFixed(2)} €</span>
              </p>
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={tvaIsCustom}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTvaIsCustom(checked);
                  if (!checked) setTva(20);
                }}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              TVA différente de 20%
            </label>
            {tvaIsCustom && (
              <input
                type="number"
                value={tva}
                onChange={(e) => setTva(parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="0.1"
                placeholder="Ex: 0"
                style={{ marginTop: '8px' }}
              />
            )}
            {!tvaIsCustom && (
              <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>TVA appliquée : 20%</small>
            )}
          </div>

          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>

          {/* Afficher la case Contrat Cadre uniquement à la création */}
          {!editingOpportunity && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="contrat-cadre-checkbox"
                checked={isContratCadre}
                onChange={e => setIsContratCadre(e.target.checked)}
              />
              <label htmlFor="contrat-cadre-checkbox" style={{ margin: 0 }}>Contrat Cadre</label>
            </div>
          )}

          <div className="form-group">
            <label>Statut</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} required>
              <option value="En Cours">En Cours</option>
              <option value="Validé">Validé</option>
              <option value="Annulé">Annulé</option>
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleClose}>Annuler</button>
            <button type="submit" disabled={!companyId || !contact || !type}>
              {editingOpportunity ? "Enregistrer" : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OpportuniteFoodinnovModal;